<template>
  <div>
    <b-card bg-variant="light"
            header="Technical evaluation"
            header-bg-variant="dark"
            header-text-variant="white">
      <b-tabs no-body>
        <b-tab active
               class="ma-1 mt-4"
               title="Technical evaluation">
          <b-row align-h="between"
                 no-gutters>
            <call-filter :id="id"
                         ref="call_filter"
                         v-model="proposals"
                         class="mb-2"
                         @load-data="loadDataFor"
                         @selected-call-change="callChange"
                         @update-proposals="refreshProposalsFilter"/>
            <button-spinner :loading="zipLoading"
                            class="mb-2"
                            variant="info"
                            @click="downloadZipWrapper">
              <template v-slot:defaultText>
                <span>PROPOSALS TO ZIP ({{ buttonZipTextAppend }})</span>
              </template>
            </button-spinner>
          </b-row>
          <!-- refresh is mixins method use to update all components -->

          <b-table :current-page="currentPage"
                   :fields="fields"
                   :items="proposals"
                   :per-page="perPage"
                   class="table-responsive"
                   head-variant="light"
                   hover
                   striped
                   thead-class="text-center"
                   @row-clicked="setShowFinalSubmit">
            <template v-slot:top-row="{fields}">
              <proposal-filter :id="id"
                               v-model="proposals"
                               :fields="fields"
                               :not-include-key="['actions', 'ordinalNumber']"
                               :selected-call="selectedCall"/>
            </template>

            <template v-slot:cell(ordinalNumber)="row">
              <base-column-ordinal-number v-bind="{index: row.index, perPage, currentPage}"/>
            </template>

            <template v-slot:cell(experiment_color.evaluated_color)="row">
              <strong :class="'text-' + getExperimentColor(row.item.experiment_color.evaluated_color)">
                {{ row.item.experiment_color.evaluated_color.toUpperCase() }}
              </strong>
            </template>

            <template v-slot:cell(experiment_color.final_evaluated_color)="row">
              <strong :class="'text-' + getExperimentColor(row.item.experiment_color.final_evaluated_color)">
                {{ row.item.experiment_color.final_evaluated_color.toUpperCase() }}
              </strong>
              <strong v-if="isAdditionalSampleDeclarationRejected(row.item)" class="d-block text-dark">
                ASD: REJECTED
              </strong>
            </template>

            <template v-slot:cell(actions)="row">
              <div @click="setShowFinalSubmit(row.item, undefined, $event)"
                   @keyup.down="setShowFinalSubmit(row.item, undefined, $event)">
                <action-download-pdf :proposal="row.item"/>

                <template v-if="isSynchrotron">
                  <action-download-additional-sample-declaration-pdf
                    v-if="isAdditionalSampleDeclarationSubmitted(row.item)"
                    :proposal="(row.item)"/>
                </template>

                <base-material-design-icon-button v-b-modal.technical_modal
                                                  :disabled="!isUserEditorOrScientist"
                                                  :title="editable(row.item) ? 'Edit evaluation' : 'View evaluation'"
                                                  icon="fact_check"
                                                  @click="setProposal(row.item)"/>

                <base-double-click-submit-button v-if="canEditOrSubmitEvaluation(row.item)"
                                                 :btn-options="submitBtnOptions"
                                                 :is-confirmation="isFinalSubmit(row.item)"
                                                 class="d-block ml-auto"
                                                 @click="submitEvaluation(row.item)"/>
              </div>
            </template>
          </b-table>
          <b-table-footer v-model="loading"
                          :current-page="currentPage"
                          :data-length="proposals.length"
                          :per-page="perPage"
                          @change-page="changePage">
            <template v-slot>
              <base-per-page-input v-model="perPage"/>
            </template>
          </b-table-footer>
        </b-tab>
      </b-tabs>
    </b-card>
    <b-modal id="technical_modal"
             :ref="modalRef"
             bg-variant="light"
             header-bg-variant="dark"
             header-text-variant="white"
             hide-footer
             lazy
             size="lg"
             title="Technical evaluation"
             @hidden="closeModal">
      <technical-modal v-model="proposal"
                       :fields="modalFields"
                       :isEditable="canEditOrSubmitEvaluation(proposal)"
                       @close-modal="closeModalSlot"/>
    </b-modal>
  </div>
</template>

<script>
  import ActionDownloadAdditionalSampleDeclarationPdf
    from '@/components/actions/ActionDownloadAdditionalSampleDeclarationPdf';
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import technicalModal from '@/components/evaluation/technical/technical_modal';
  import options from '@/json/feasibility';
  import submitBtnOptions from '@/json/doubleClickSubmitButton';
  import callFilter from '@/components/call_filter';
  import ProposalFilter from '@/components/ProposalFilter';
  import BTableFooter from '@/components/BTableFooter';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import ButtonSpinner from '@/components/buttonSpinner';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseDoubleClickSubmitButton from '@/components/base-components/BaseDoubleClickSubmitButton';
  import BaseMaterialDesignIconButton from '@/components/base-components/base-icons/BaseMaterialDesignIconButton';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMarkSafety';

  export default {
    components: {
      ActionDownloadAdditionalSampleDeclarationPdf,
      ActionDownloadPdf,
      BaseColumnOrdinalNumber,
      BaseDoubleClickSubmitButton,
      BaseMaterialDesignIconButton,
      BasePerPageInput,
      ButtonSpinner,
      technicalModal,
      callFilter,
      ProposalFilter,
      BTableFooter,
    },
    mixins: [infrastructureMixin],
    props: {
      fields: {
        type: Array,
        default: () => [],
      },
      modalFields: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        // necessary for proposal and calls filter feature
        proposals: [],
        id: 1,
        selectedCall: '',
        // end

        // necessary for b-table feature
        currentPage: 1,
        perPage: 20,
        // end

        // use to handle zip filtered proposals
        allProposalsLength: 0,
        buttonZipTextAppend: 'ALL',

        // use to hide modal
        modalRef: 'modalRef',

        loading: false,
        zipLoading: false,
        proposal: {},
        index: 0,
        options: options.options,
        trafficLightsOptions,
        submitBtnOptions,
        finalSubmit: '',
      };
    },
    computed: {
      isSynchrotron() {
        return this.infrastructure === 'synchrotron';
      },
      isUserEditor() {
        const permissions = this.$store.getters['login/getLoggedUserAttribute']('permissions') || [];
        if (this.infrastructure === 'synchrotron') {
          return permissions.includes('EDIT_TEF_EVAL');
        }
        return permissions.includes('EDIT_CRYO_TEF_EVAL');
      },
      isUserScientist() {
        const permissions = this.$store.getters['login/getLoggedUserAttribute']('permissions') || [];
        if (this.infrastructure === 'synchrotron') {
          return permissions.includes('READ_TEF_EVAL');
        }
        return permissions.includes('READ_CRYO_TEF_EVAL');
      },
      isUserEditorOrScientist() {
        return this.isUserEditor || this.isUserScientist;
      },
    },
    methods: {
      changePage(val) {
        this.currentPage = val;
      },
      closeModal() {
        this.updateProposal();
        this.proposal = {};
      },
      async downloadZipWrapper() {
        this.zipLoading = true;
        const params = [];
        if (this.infrastructure === 'synchrotron') {
          params.push({ endstation: true });
        }

        // eslint-disable-next-line camelcase
        params.push({ proposal_id_range: this.filteredProposalIDRange(this.proposals) });
        params.push({ call: this.$refs.call_filter?.selectedCall ?? null });

        await this.downloadZip(...params);
        this.zipLoading = false;
      },
      editable(proposal) {
        if (proposal?.technical_evaluation?.states) {
          return proposal.technical_evaluation.states.name !== 'submitted';
        }
        return true;
      },
      getExperimentColor(status) {
        let variant = 'secondary';
        this.trafficLightsOptions.forEach((option) => {
          if (option.value === status) {
            variant = option.disabledVariant;
          }
        });
        return variant;
      },
      isAdditionalSampleDeclarationSubmitted(item) {
        return item.additional_sample_declaration?.states?.name === 'submitted';
      },
      isAdditionalSampleDeclarationRejected(item) {
        return item.experiment_color.final_evaluated_color
          && (item.additional_sample_declaration.safety_evaluation.status === 'REJECTED'
            || item.additional_sample_declaration.radiation_evaluation.status === 'REJECTED');
      },
      isFeasibilitySet(proposal) {
        const { feasibility } = proposal.technical_evaluation;
        return !(feasibility === null || typeof feasibility === 'undefined');
      },
      async loadDataFor(call) {
        const callParam = { call };
        this.loading = true;
        try {
          const response = await this.axios.get(
            `/documents/technical/${this.infrastructure}`,
            { params: callParam },
          );
          if (response.status !== 204) {
            this.proposals = response.data.documents;
            const { authors } = response.data;
            this.proposals = this.proposals.map((proposal) => {
              const proposalId = this.getId(proposal);
              const { experiment_color = {} } = proposal;
              // eslint-disable-next-line camelcase
              experiment_color.evaluated_color = experiment_color.evaluated_color || '';
              // eslint-disable-next-line camelcase
              experiment_color.final_evaluated_color = experiment_color.final_evaluated_color || '';
              return {
                ...proposal,
                // eslint-disable-next-line camelcase
                technical_evaluation: proposal.technical_evaluation || {},
                // eslint-disable-next-line camelcase
                experiment_color,
                author: `${authors[proposalId].name}\n${authors[proposalId].email}`,
              };
            });
          } else {
            this.proposals = [];
          }
          // update data in proposal-filter, call-filter component
          this.$emit('setup-proposal', this.proposals);
          this.allProposalsLength = this.proposals?.length;
          this.loading = false;
        } catch {
          this.$notify({ type: 'error', title: 'Communication error' });
        }
      },
      setProposal(proposal) {
        this.proposal = proposal;
      },
      updateButtonZipTextAppend() {
        const filteredProposalsLength = this.proposals?.length;
        this.buttonZipTextAppend = this.allProposalsLength === filteredProposalsLength ? 'ALL'
          : filteredProposalsLength.toString();
      },
      filteredProposalIDRange(documents) {
        const uniqueProposalIds = new Set();
        documents.forEach((obj) => {
          uniqueProposalIds.add(obj.document_specification?.document_id);
        });
        return Array.from(uniqueProposalIds);
      },
      statusMessage(param) {
        this.$notify({ type: param.status || 'error', title: param.message || 'Feasibility not set' });
      },
      canEditOrSubmitEvaluation(proposal) {
        return this.editable(proposal) && this.isUserEditor;
      },
      isFinalSubmit(proposal) {
        return this.getId(proposal) === this.finalSubmit;
      },
      setShowFinalSubmit(proposal, unusedIndex, event) {
        this.finalSubmit = '';
        if (event.target.id === this.submitBtnOptions.attempt.icon.id) {
          this.finalSubmit = this.getId(proposal);
        }
      },
      submitEvaluation(proposal) {
        this.setProposal(proposal);
        if (this.isFeasibilitySet(this.proposal)) {
          const proposalId = this.getId(this.proposal);
          const url = `/documents/${proposalId}/technical/${this.infrastructure}`;
          this.axios.patch(url)
            .then((response) => {
              if (response.data.error) {
                this.statusMessage({ message: response.data.error });
              } else {
                this.proposal.technical_evaluation.states.name = 'submitted';
                this.statusMessage({ status: 'success', message: response.data.msg });
              }
            })
            .catch((error) => {
              this.statusMessage({ message: error.response.data.error });
            });
        } else {
          this.statusMessage({});
        }
      },
      updateProposal() {
        for (let i = 0; i < this.proposals.length; i += 1) {
          if (this.getId(this.proposals[i]) === this.getId(this.proposal)) {
            this.$set(this.proposals, i, this.proposal);
          }
        }
      },
    },
    watch: {
      proposals() {
        this.updateButtonZipTextAppend();
      },
    },
  };
</script>

<style lang="scss"
       scoped>
  .card {
    margin: 2rem;
  }

  ::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    text-align: center !important;
  }

  ::-moz-placeholder { /* Firefox 19+ */
    text-align: center !important;
  }

  :-ms-input-placeholder { /* IE 10+ */
    text-align: center !important;
  }

  :-moz-placeholder { /* Firefox 18- */
    text-align: center !important;
  }
</style>
